import {
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/Edit';
import { DeleteForeverOutlined } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadClasificacionMovimientos } from '../../redux/ducks/clasificacionMovimientos';
import { Controls } from '../../components/controls/Controls';
import { Form, useForm } from '../../components/useForm';
import { setSnackbar } from '../../redux/ducks/snackbar';
import http from '../../http-common';
import Popup from '../../components/Popup';
import GastosForm from '../Viaje/GastosForm';
import { buscarControlIngreso } from './controlIngresoSync';

const initialFieldValues = {
    id: 0,
    fecha: null,
    referencia: '',
    tipo: '',
    factura: '',
    descripcion: '',
    folio: '',
    deposito: 0,
    retiro: 0,
    saldoAnterior: 0,
    saldoActual: 0,
    reflejadoIngresos: false,
};

const headCellsGastos = [
    { id: 'tramoId', label: 'No. Tramo' },
    { id: 'tipoPago', label: 'Tipo de Gasto' },
    { id: 'descripcion', label: 'Descripción' },
    { id: 'fechaPago', label: 'Fecha' },
    { id: 'cantidad', label: 'Cantidad' },
    { id: 'idMovimientoBancario', label: 'ID Mov. Bancario' },
    { id: 'idCuentaBancaria', label: 'ID Cuenta' },
    { id: 'actions', label: 'Acciones' },
];

const formatMoneda = (value) => {
    if (value == null) return '$ 0.00';
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(value);
};

// Los gastos agregados en esta sesión aún no tienen id (null) hasta guardar; se identifican por localId
const mismoGasto = (a, b) => {
    if (a.localId || b.localId) return a.localId === b.localId;
    return a.id != null && a.id === b.id;
};

const MESES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const formatFecha = (fecha) => {
    if (!fecha) return '';
    let date;
    if (typeof fecha === 'string') {
        date = new Date(fecha.includes('T') ? fecha : `${fecha}T00:00:00`);
    } else {
        // Date object or moment-like object
        date = new Date(fecha);
    }
    if (Number.isNaN(date.getTime())) return String(fecha);
    return `${date.getDate()}-${MESES_ES[date.getMonth()]}-${date.getFullYear()}`;
};

export default function MovimientosBancariosForm(props) {
    const {
        addOrEdit, recordForEdit, idCuenta, saldoCuenta, nuevoSaldoActual, checkUnsavedRef,
    } = props;

    const dispatch = useDispatch();
    const clasificaciones = useSelector(
        (state) => state.clasificacionMovimientos.clasificaciones,
    );
    const clasificacionesOptions = clasificaciones.map((c) => ({
        id: c.clasificacion,
        title: c.clasificacion,
    }));

    useEffect(() => {
        dispatch(loadClasificacionMovimientos());
    }, [dispatch]);

    const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
    const [unapprovePassword, setUnapprovePassword] = useState('');
    const [pendingValues, setPendingValues] = useState(null);

    const [originalReflejadoIngresos, setOriginalReflejadoIngresos] = useState(false);
    const [deleteControlIngresoPending, setDeleteControlIngresoPending] = useState(false);

    const [folioValido, setFolioValido] = useState(null);
    const folioValidoRef = useRef(null); // ref síncrono para leer folioValido en handleSubmit sin stale closure
    const lastValidatedFolioRef = useRef(null); // evita re-validar el mismo folio en onBlur
    const gastosModificadosRef = useRef(false); // true si otrosGastos cambió y necesita guardarse en viaje
    const gastosNuevosAgregadosRef = useRef(false); // true si el usuario agregó al menos 1 gasto nuevo en esta sesión
    const saldoRefrescadoEnEdicionRef = useRef(false); // true si ya se actualizó saldoAnterior al editar retiro/deposito
    const movimientoIdRef = useRef(null); // ID del movimiento bancario actual (para adjuntarlo a los gastos)
    const originalFolioRef = useRef(null); // folio que tenía el registro al abrirse para editar
    const originalDepositoRef = useRef(0); // deposito original al abrir para editar
    const originalRetiroRef = useRef(0); // retiro original al abrir para editar
    const [isDirty, setIsDirty] = useState(false); // true cuando el usuario modificó algún campo en modo edición
    const [gastosModificados, setGastosModificados] = useState(false); // true cuando se agregó o editó un gasto
    const depositoClearPendingRef = useRef(false); // true = primer keystroke en deposito debe limpiar el campo
    const retiroClearPendingRef = useRef(false); // true = primer keystroke en retiro debe limpiar el campo
    const localIdRef = useRef(0); // contador para el localId de gastos nuevos
    const otrosGastosRef = useRef([]); // ref síncrono para leer otrosGastos en handleSubmit sin stale closure
    const [viajeAprobadoDialogOpen, setViajeAprobadoDialogOpen] = useState(false);
    const [gastosRequeridosDialogOpen, setGastosRequeridosDialogOpen] = useState(false);
    const [viaje, setViaje] = useState(null); // viaje completo (necesario para guardar)
    const [otrosGastos, setOtrosGastos] = useState([]); // lista local editable
    const [gastosOriginales, setGastosOriginales] = useState([]); // snapshot al abrir el modal
    const [tramos, setTramos] = useState([]); // tramos del viaje (para GastosForm)
    const [openGastosPopup, setOpenGastosPopup] = useState(false);
    const [gastoParaEditar, setGastoParaEditar] = useState(null);

    // ----- validar folio + cargar viaje -----
    const validarFolio = async (folio) => {
        lastValidatedFolioRef.current = folio; // registra el folio que se está validando
        if (!folio) {
            folioValidoRef.current = null;
            setFolioValido(null);
            otrosGastosRef.current = [];
            setOtrosGastos([]);
            setGastosOriginales([]);
            setTramos([]);
            setViaje(null);
            return;
        }
        try {
            const validResp = await http.get(`/viajes/validarFolio/${folio}`);
            const esValido = validResp.data === true || validResp.data === 'true';
            folioValidoRef.current = esValido; // actualizar ref síncronamente
            setFolioValido(esValido);
            if (esValido) {
                const viajeResp = await http.get(`/viajes/${folio}`);
                const viajeData = viajeResp.data;
                const gastosRaw = viajeData?.otrosGastos || [];
                // saldoAplicado no persiste en el backend; se reconstruye desde idCuentaBancaria
                const gastos = gastosRaw.map((g) => ({
                    ...g,
                    saldoAplicado: !!(g.idCuentaBancaria),
                }));
                setViaje(viajeData);
                otrosGastosRef.current = gastos; // actualizar ref síncronamente
                setOtrosGastos(gastos);
                setGastosOriginales(gastos); // snapshot del estado inicial
                setTramos(viajeData?.tramos || []);
            } else {
                setViaje(null);
                otrosGastosRef.current = [];
                setOtrosGastos([]);
                setGastosOriginales([]);
                setTramos([]);
                dispatch(setSnackbar(true, 'error', 'El Folio no existe'));
            }
        } catch {
            folioValidoRef.current = false;
            setFolioValido(false);
            setViaje(null);
            otrosGastosRef.current = [];
            setOtrosGastos([]);
            setTramos([]);
            dispatch(setSnackbar(true, 'error', 'El Folio no existe'));
        }
    };

    // ----- guardar lista modificada de gastos en el backend -----
    // overrideMovimientoId: usar este ID en lugar del values.id (útil cuando el movimiento es nuevo
    //   y aún no tiene ID: se llama de nuevo después de que el backend lo asigna).
    // Retorna true si se guardó correctamente, false si falló (ej. viaje APROBADO)
    const guardarGastosEnViaje = async (nuevosGastos, overrideMovimientoId = null) => {
        if (!viaje) return true; // sin viaje no hay nada que guardar, continuar flujo
        // Añadir idMovimientoBancario a cada gasto si el ID del movimiento ya está disponible
        // overrideMovimientoId es pasado explícitamente por los call sites para evitar captura de values antes de su definición
        const idMov = overrideMovimientoId ?? null;
        // localId solo sirve para identificar gastos nuevos en pantalla; no se envía al backend
        // eslint-disable-next-line no-unused-vars
        const sinLocalId = nuevosGastos.map(({ localId, ...g }) => g);
        const gastosParaBackend = idMov !== null
            // Solo asignar idMovimientoBancario/idCuentaBancaria a los gastos recién agregados en esta sesión
            // (id === null = aún no persistidos). Los pre-existentes (id numérico real) se
            // dejan intactos: pueden tener null (agregados desde la pantalla de Viaje) o un
            // idMovimientoBancario propio que no debe sobreescribirse.
            ? sinLocalId.map((g) => ({
                ...g,
                idMovimientoBancario: (g.id === null) ? idMov : g.idMovimientoBancario,
                idCuentaBancaria: (g.id === null) ? idCuenta : g.idCuentaBancaria,
            }))
            : sinLocalId;
        try {
            const viajeActualizado = { ...viaje, otrosGastos: gastosParaBackend };
            // validateStatus:true evita que el interceptor global maneje el error;
            // revisamos el status manualmente para mostrar el mensaje correcto
            const response = await http.post('/viajes?password=', viajeActualizado, {
                headers: { 'Content-Type': 'application/json' },
                validateStatus: () => true,
            });
            if (!response || response.status < 200 || response.status >= 300) {
                setViajeAprobadoDialogOpen(true);
                return false;
            }
            setViaje(viajeActualizado);
            return true;
        } catch {
            setViajeAprobadoDialogOpen(true);
            return false;
        }
    };

    // ----- obtener saldo actual de la cuenta desde el backend -----
    const obtenerSaldoCuentaActual = async () => {
        try {
            const resp = await http.get(`/cuentas-bancarias/${idCuenta}`);
            return resp.data?.saldo ?? saldoCuenta ?? 0;
        } catch {
            return saldoCuenta ?? 0; // fallback al prop si falla la llamada
        }
    };

    // ----- callback que recibe GastosForm al guardar -----
    const addOrEditGasto = async (gasto) => {
        let nuevosGastos;
        const esEdicion = !!gasto.localId || (!!gasto.id && gasto.id !== 0);
        if (!esEdicion) {
            gastosNuevosAgregadosRef.current = true; // el usuario añadió al menos 1 gasto nuevo
            localIdRef.current += 1;
            nuevosGastos = otrosGastos.concat({ ...gasto, id: null, localId: `nuevo-${localIdRef.current}` });
        } else {
            const idx = otrosGastos.findIndex((g) => mismoGasto(g, gasto));
            nuevosGastos = [
                ...otrosGastos.slice(0, idx),
                gasto,
                ...otrosGastos.slice(idx + 1),
            ];
        }
        // En modo edición, reflejar idMovimientoBancario e idCuentaBancaria en el estado local para nuevos gastos
        const idMov = movimientoIdRef.current;
        if (idMov !== null) {
            nuevosGastos = nuevosGastos.map((g) => ({
                ...g,
                idMovimientoBancario: g.id === null ? idMov : g.idMovimientoBancario,
                idCuentaBancaria: g.id === null ? idCuenta : g.idCuentaBancaria,
            }));
        }
        otrosGastosRef.current = nuevosGastos;
        setOtrosGastos(nuevosGastos);
        setOpenGastosPopup(false);
        gastosModificadosRef.current = true;
        setGastosModificados(true);
        // movimientoIdRef tiene el ID del movimiento (null si es nuevo; se actualiza vía onMovimientoSaved)
        await guardarGastosEnViaje(nuevosGastos, movimientoIdRef.current);
    };

    // ----- toggle saldoAplicado en gasto (solo estado local; se persiste al Guardar) -----
    // Al marcar: suma cantidad al retiro. Al desmarcar: resta cantidad del retiro.
    const toggleSaldoAplicado = async (gasto) => {
        const nuevoValor = !gasto.saldoAplicado;
        const nuevosGastos = otrosGastos.map((g) => (
            mismoGasto(g, gasto)
                ? { ...g, saldoAplicado: nuevoValor, idCuentaBancaria: nuevoValor ? idCuenta : null }
                : g
        ));
        otrosGastosRef.current = nuevosGastos;
        setOtrosGastos(nuevosGastos);
        gastosModificadosRef.current = true; // marcar que hay cambios pendientes en gastos

        // Consultar saldo actualizado del backend para evitar valor cacheado
        let saldoBase = saldoCuenta ?? 0;
        try {
            const resp = await http.get(`/cuentas-bancarias/${idCuenta}`);
            saldoBase = resp.data?.saldo ?? saldoBase;
        } catch {
            // fallback al prop si falla la llamada
        }

        const cantidad = parseFloat(gasto.cantidad) || 0;
        const delta = nuevoValor ? cantidad : -cantidad;
        // Solo considerar cambios respecto al estado original (snapshot al abrir el modal).
        // saldoBase ya refleja los gastos que estaban marcados al guardar anteriormente.
        const totalNuevamenteMarcado = nuevosGastos
            .filter((g) => g.saldoAplicado && !gastosOriginales.find((og) => og.id === g.id)?.saldoAplicado)
            .reduce((sum, g) => sum + (parseFloat(g.cantidad) || 0), 0);
        const totalNuevamenteDesmarcado = nuevosGastos
            .filter((g) => !g.saldoAplicado && gastosOriginales.find((og) => og.id === g.id)?.saldoAplicado)
            .reduce((sum, g) => sum + (parseFloat(g.cantidad) || 0), 0);
        const saldoActualCalculado = saldoBase - totalNuevamenteMarcado + totalNuevamenteDesmarcado;
        // eslint-disable-next-line no-use-before-define
        setValues((prev) => ({
            ...prev,
            retiro: Math.max(0, (parseFloat(prev.retiro) || 0) + delta),
            saldoAnterior: saldoBase,
            saldoActual: saldoActualCalculado,
        }));
    };

    // ----- eliminar gasto -----
    const [confirmDeleteGastoOpen, setConfirmDeleteGastoOpen] = useState(false);
    const gastoAEliminarRef = useRef(null);

    const eliminarGasto = (gasto) => {
        gastoAEliminarRef.current = gasto;
        setConfirmDeleteGastoOpen(true);
    };

    const confirmarEliminarGasto = async () => {
        const gasto = gastoAEliminarRef.current;
        if (!gasto) return;
        setConfirmDeleteGastoOpen(false);
        gastoAEliminarRef.current = null;
        const nuevosGastos = otrosGastos.filter((g) => !mismoGasto(g, gasto));
        otrosGastosRef.current = nuevosGastos;
        setOtrosGastos(nuevosGastos);
        gastosModificadosRef.current = true;
        await guardarGastosEnViaje(nuevosGastos, movimientoIdRef.current);
    };

    // ----- form principal -----
    const {
        values,
        setValues,
        errors,
        setErrors,
        handleInputChange,
        resetForm,
        // eslint-disable-next-line no-use-before-define
    } = useForm(initialFieldValues, true, validate);

    const handleInputChangeDirty = (e) => {
        setIsDirty(true);
        handleInputChange(e);
    };

    function validate(fieldValues = values) {
        const temp = { ...errors };
        if ('fecha' in fieldValues) {
            const f = fieldValues.fecha;
            const fechaValida = f && !(f instanceof Date && Number.isNaN(f.getTime()));
            temp.fecha = fechaValida ? '' : 'Campo requerido';
        }
        if ('tipo' in fieldValues) {
            temp.tipo = fieldValues.tipo ? '' : 'Campo requerido';
        }
        setErrors({ ...temp });
        if (fieldValues === values) {
            return Object.values(temp).every((x) => x === '');
        }
        return true;
    }

    // Si el registro estaba APROBADO y se des-aprobó en este guardado, guarda el password
    // para poder re-aprobarlo si el usuario cancela después.
    const passwordDesaprobacionRef = useRef(null);
    const valoresPorGuardarRef = useRef(null);

    const restaurarAprobacion = async () => {
        const password = passwordDesaprobacionRef.current;
        passwordDesaprobacionRef.current = null;
        if (!password || !values.id) return;
        await http.post('/movimientos-bancarios/aprobarMovimiento', [
            { id: values.id, status: 'APROBADO', password },
        ], { validateStatus: () => true });
    };

    // Pasos comunes del guardado, después de la validación y del password (si aplicaba)
    const continuarGuardado = async (vals) => {
        const isNew = !vals.id || vals.id === 0;
        const currentReflejado = !!vals.reflejadoIngresos;

        // Caso: editar + estaba reflejado + ahora se desmarcó → confirmar borrado en control ingresos
        if (!isNew && originalReflejadoIngresos && !currentReflejado) {
            valoresPorGuardarRef.current = vals;
            setDeleteControlIngresoPending(true);
            return;
        }

        // Caso: editar + NO estaba reflejado + ahora se marcó → verificar que no exista ya
        if (!isNew && !originalReflejadoIngresos && currentReflejado) {
            const existente = await buscarControlIngreso(vals.id, idCuenta);
            if (existente) {
                await restaurarAprobacion();
                dispatch(setSnackbar(true, 'error', 'Este ID ya existe en control ingresos, no se puede guardar'));
                return;
            }
        }

        // Determinar si el padre debe crear el registro en control ingresos
        const shouldCreate = currentReflejado && (isNew || !originalReflejadoIngresos);

        if (folioValidoRef.current === true && gastosModificadosRef.current) {
            // movimientoIdRef: en edit mode tiene el ID; en new mode es null (se pasará vía onMovimientoSaved)
            const gastosGuardados = await guardarGastosEnViaje(otrosGastosRef.current, movimientoIdRef.current);
            if (!gastosGuardados) return; // viaje aprobado, detener flujo
        }

        // Callback para registros NUEVOS: una vez que el movimiento se guarda y obtenemos su ID,
        // actualizamos los gastos del viaje para que tengan idMovimientoBancario correcto.
        // Para edición, guardarGastosEnViaje ya usa movimientoIdRef.current directamente.
        const onMovimientoSaved = (isNew && viaje && otrosGastosRef.current.length > 0)
            ? async (savedId) => {
                movimientoIdRef.current = savedId; // actualizar ref para cualquier llamada posterior
                await guardarGastosEnViaje(otrosGastosRef.current, savedId);
            }
            : null;

        passwordDesaprobacionRef.current = null;
        addOrEdit(vals, resetForm, shouldCreate, onMovimientoSaved);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar campos requeridos visibles con mensaje claro
        const f = values.fecha;
        const fechaValida = f && !(f instanceof Date && Number.isNaN(f.getTime()));
        if (!fechaValida && !values.tipo) {
            dispatch(setSnackbar(true, 'error', 'Los campos Fecha y Clasificación son requeridos'));
            return;
        }
        if (!fechaValida) {
            dispatch(setSnackbar(true, 'error', 'El campo Fecha es requerido'));
            return;
        }
        if (!values.tipo) {
            dispatch(setSnackbar(true, 'error', 'El campo Clasificación es requerido'));
            return;
        }

        if (!validate()) return;

        // Si hay folio pero la validación async todavía no terminó, esperar ahora
        // (condición de carrera: usuario escribe folio → salta a retiro → guarda rápido)
        if (values.folio && folioValidoRef.current === null) {
            await validarFolio(values.folio);
        }

        // Validación: folio presente + registro nuevo (sin folio original) + no agregó gastos nuevos
        // gastosNuevosAgregadosRef rastrea si el usuario añadió al menos 1 gasto en esta sesión;
        // los gastos pre-existentes del viaje no cuentan.
        if (folioValidoRef.current === true && !originalFolioRef.current
            && !gastosNuevosAgregadosRef.current) {
            setGastosRequeridosDialogOpen(true);
            return;
        }

        // Si el registro ya está APROBADO, pedir password antes de guardar
        if (recordForEdit?.status === 'APROBADO') {
            setPendingValues(values);
            setUnapproveDialogOpen(true);
            return;
        }

        await continuarGuardado(values);
    };

    const handleCancelDeleteControlIngreso = async () => {
        setDeleteControlIngresoPending(false);
        valoresPorGuardarRef.current = null;
        await restaurarAprobacion();
    };

    const handleConfirmDeleteControlIngreso = async () => {
        const vals = valoresPorGuardarRef.current || values;
        setDeleteControlIngresoPending(false);
        let borrado = true;
        try {
            const record = await buscarControlIngreso(vals.id, idCuenta);
            if (record) {
                const delResp = await http.delete(`/control-ingresos/${record.id}`, { validateStatus: () => true });
                borrado = delResp.status >= 200 && delResp.status < 300;
                if (!borrado) {
                    const msg = typeof delResp.data === 'string' ? delResp.data : delResp.data?.message;
                    dispatch(setSnackbar(true, 'error', msg || 'No se pudo borrar el registro en Control Ingresos'));
                }
            }
        } catch {
            borrado = false;
            dispatch(setSnackbar(true, 'error', 'No se pudo borrar el registro en Control Ingresos'));
        }
        if (!borrado) {
            valoresPorGuardarRef.current = null;
            await restaurarAprobacion();
            return;
        }
        valoresPorGuardarRef.current = null;
        passwordDesaprobacionRef.current = null;
        if (folioValidoRef.current === true && gastosModificadosRef.current) {
            const gastosGuardados = await guardarGastosEnViaje(otrosGastosRef.current, movimientoIdRef.current);
            if (!gastosGuardados) return; // viaje aprobado, detener flujo
        }
        addOrEdit({ ...vals, reflejadoIngresos: false }, resetForm, false);
    };

    const handleUnapproveAndSave = async () => {
        // Primero cambiar status a PENDIENTE (valida el password)
        let resp;
        try {
            resp = await http.post('/movimientos-bancarios/aprobarMovimiento', [
                { id: pendingValues.id, status: 'PENDIENTE', password: unapprovePassword },
            ], { validateStatus: () => true });
        } catch {
            resp = null;
        }
        if (!resp || resp.status < 200 || resp.status >= 300) {
            const msg = typeof resp?.data === 'string' ? resp.data : resp?.data?.message;
            dispatch(setSnackbar(true, 'error', msg || 'Error al des-aprobar el movimiento'));
            return;
        }
        const vals = { ...pendingValues, status: 'PENDIENTE' };
        passwordDesaprobacionRef.current = unapprovePassword;
        setUnapproveDialogOpen(false);
        setUnapprovePassword('');
        setPendingValues(null);
        // Continúa con los mismos pasos que un guardado normal (incluye la confirmación
        // de borrado en control ingresos si se desmarcó "reflejado")
        await continuarGuardado(vals);
    };

    useEffect(() => {
        lastValidatedFolioRef.current = null; // resetear al cambiar el registro
        gastosModificadosRef.current = false; // resetear bandera de gastos modificados
        gastosNuevosAgregadosRef.current = false; // resetear bandera de gastos nuevos
        setGastosModificados(false);
        saldoRefrescadoEnEdicionRef.current = false; // resetear para que la próxima edición vuelva a refrescar
        movimientoIdRef.current = recordForEdit?.id || null; // ID del movimiento actual (null si es nuevo)
        originalFolioRef.current = recordForEdit?.folio || null; // guardar folio original al abrir
        originalDepositoRef.current = parseFloat(recordForEdit?.deposito) || 0;
        originalRetiroRef.current = parseFloat(recordForEdit?.retiro) || 0;
        setIsDirty(false);
        depositoClearPendingRef.current = !!(recordForEdit?.id);
        retiroClearPendingRef.current = !!(recordForEdit?.id);
        folioValidoRef.current = null;
        setFolioValido(null);
        otrosGastosRef.current = [];
        setOtrosGastos([]);
        setGastosOriginales([]);
        setTramos([]);
        setViaje(null);
        if (recordForEdit != null) {
            const fechaNormalizada = (typeof recordForEdit.fecha === 'string' && !recordForEdit.fecha.includes('T'))
                ? new Date(`${recordForEdit.fecha}T00:00:00`)
                : recordForEdit.fecha;
            setValues({ ...recordForEdit, fecha: fechaNormalizada });
            setOriginalReflejadoIngresos(!!recordForEdit.reflejadoIngresos);
            // Si el registro ya tiene folio, cargar los otros gastos automáticamente
            if (recordForEdit.folio) {
                validarFolio(recordForEdit.folio);
            }
        } else {
            setOriginalReflejadoIngresos(false);
            // Registro nuevo: pre-llenar saldoAnterior y saldoActual con el saldo calculado
            const saldoBase = nuevoSaldoActual ?? saldoCuenta ?? 0;
            setValues((prev) => ({
                ...prev,
                saldoAnterior: saldoBase,
                saldoActual: saldoBase,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordForEdit]);

    // Registra en checkUnsavedRef una función con closure FRESCO sobre el estado del form.
    // Sin array de dependencias → se actualiza en cada render → nunca hay closure stale.
    // El padre llama a esta función al intentar cerrar el popup.
    useEffect(() => {
        if (!checkUnsavedRef) return;
        checkUnsavedRef.current = () => {
            // Aplica tanto para NUEVO como para EDITAR: si se agregó al menos 1 gasto
            // en esta sesión y no se guardó el formulario, advertir antes de cerrar.
            const hasUnsaved = gastosNuevosAgregadosRef.current || gastosModificadosRef.current;
            return {
                hasUnsaved,
                // cleanup: restaurar gastos originales en el viaje (elimina los de esta sesión)
                cleanup: async () => {
                    if (!viaje) return;
                    try {
                        await http.post('/viajes?password=', {
                            ...viaje,
                            otrosGastos: gastosOriginales,
                        }, {
                            headers: { 'Content-Type': 'application/json' },
                            validateStatus: () => true,
                        });
                    } catch {
                        // Si falla la limpieza, el viaje queda con gastos huérfanos
                    }
                },
            };
        };
    });

    return (
        <>
            <Form onSubmit={handleSubmit}>
                <Grid container>
                    {/* ---- columna izquierda ---- */}
                    <Grid item xs={6}>
                        <Controls.DatePicker
                            error={errors.fecha}
                            label='Fecha'
                            name='fecha'
                            onChange={handleInputChangeDirty}
                            value={values.fecha}
                        />
                        <Controls.Input
                            error={errors.referencia}
                            label='Cliente'
                            name='referencia'
                            onChange={handleInputChangeDirty}
                            value={values.referencia}
                        />
                        <Controls.Select
                            error={errors.tipo}
                            label='Clasificación'
                            name='tipo'
                            onChange={handleInputChangeDirty}
                            options={clasificacionesOptions}
                            value={values.tipo}
                        />
                        <Controls.Input
                            error={errors.factura}
                            helperText={`${(values.factura || '').length}/1500`}
                            inputProps={{ maxLength: 1500 }}
                            label='Factura'
                            multiline
                            name='factura'
                            onChange={handleInputChangeDirty}
                            rows={3}
                            value={values.factura}
                        />
                        <Box alignItems='center' display='flex' gap={1}>
                            <Controls.Input
                                error={errors.folio}
                                inputProps={{ min: 0, step: 1 }}
                                label='Folio'
                                name='folio'
                                onBlur={() => {
                                    // Solo validar si el folio cambió desde la última validación
                                    // Esto evita recargar los gastos del backend cuando el usuario
                                    // hace clic en otro elemento del form (ej: el checkbox de gastos)
                                    if (values.folio !== lastValidatedFolioRef.current) {
                                        validarFolio(values.folio);
                                    }
                                }}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                        setIsDirty(true);
                                        folioValidoRef.current = null;
                                        setFolioValido(null);
                                        otrosGastosRef.current = [];
                                        setOtrosGastos([]);
                                        setTramos([]);
                                        setViaje(null);
                                        handleInputChange(e);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        validarFolio(values.folio);
                                    }
                                }}
                                sx={{
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                                type='number'
                                value={values.folio}
                            />
                            {folioValido === true && (
                                <CheckCircleIcon sx={{ color: 'green', fontSize: 28 }} />
                            )}
                            {folioValido === false && (
                                <CancelIcon sx={{ color: 'red', fontSize: 28 }} />
                            )}
                        </Box>
                    </Grid>

                    {/* ---- columna derecha ---- */}
                    <Grid item xs={6}>
                        <Controls.Textarea
                            error={errors.descripcion}
                            label='Descripción'
                            maxRows={4}
                            minRows={3}
                            name='descripcion'
                            onChange={handleInputChangeDirty}
                            value={values.descripcion}
                        />
                        <Controls.Input
                            disabled={!!values.folio || !!(parseFloat(values.retiro))}
                            error={errors.deposito}
                            inputProps={{ step: '0.01' }}
                            label='Depósito'
                            name='deposito'
                            onChange={(e) => {
                                setIsDirty(true);
                                const depositoVal = parseFloat(e.target.value) || 0;
                                setValues((prev) => ({
                                    ...prev,
                                    deposito: e.target.value,
                                    saldoActual: (parseFloat(prev.saldoAnterior) || 0) + depositoVal,
                                }));
                            }}
                            sx={{
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                '& input[type=number]': { MozAppearance: 'textfield' },
                            }}
                            type='number'
                            value={values.deposito}
                        />
                        <Controls.Input
                            disabled={!!(parseFloat(values.deposito))}
                            error={errors.retiro}
                            inputProps={{ step: '0.01' }}
                            label='Retiro'
                            name='retiro'
                            onChange={(e) => {
                                setIsDirty(true);
                                const retiroVal = parseFloat(e.target.value) || 0;
                                setValues((prev) => ({
                                    ...prev,
                                    retiro: e.target.value,
                                    saldoActual: (parseFloat(prev.saldoAnterior) || 0) - retiroVal,
                                }));
                            }}
                            sx={{
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                '& input[type=number]': { MozAppearance: 'textfield' },
                            }}
                            type='number'
                            value={values.retiro}
                        />
                        <Controls.Input
                            inputProps={{ readOnly: true }}
                            label='Saldo Anterior'
                            name='saldoAnterior'
                            onChange={() => {}}
                            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                            value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(values.saldoAnterior) || 0)}
                        />
                        <Controls.Input
                            inputProps={{ readOnly: true }}
                            label='Saldo Movimiento'
                            name='saldoActual'
                            onChange={() => {}}
                            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' } }}
                            value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(values.saldoActual) || 0)}
                        />
                        <Box alignItems='center' display='flex' gap={1} sx={{ mt: 1 }}>
                            <Checkbox
                                checked={!!values.reflejadoIngresos}
                                disabled={!(parseFloat(values.deposito) > 0)}
                                onChange={(e) => {
                                    setIsDirty(true);
                                    // eslint-disable-next-line no-use-before-define
                                    setValues((prev) => ({ ...prev, reflejadoIngresos: e.target.checked }));
                                }}
                                size='small'
                            />
                            <Typography
                                sx={{ color: parseFloat(values.deposito) > 0 ? 'inherit' : '#aaa', fontSize: '0.85rem' }}
                                variant='body2'
                            >
                                Reflejado en control ingresos
                            </Typography>
                        </Box>
                        <div>
                            <Controls.Button
                                disabled={!!(recordForEdit?.id) && !isDirty && !gastosModificados}
                                text='Guardar'
                                type='submit'
                            />
                            <Controls.Button
                                color='default'
                                onClick={resetForm}
                                text='Reset'
                            />
                        </div>
                    </Grid>

                    {/* ---- tabla Otros Gastos ---- */}
                    {folioValido === true && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box alignItems='center' display='flex' justifyContent='space-between' mb={1}>
                                <Typography
                                    sx={{ color: '#333996', fontWeight: 'bold' }}
                                    variant='subtitle1'
                                >
                                    {'Otros Gastos — Folio '}
                                    {values.folio}
                                </Typography>
                                <Controls.Button
                                    onClick={() => { setGastoParaEditar(null); setOpenGastosPopup(true); }}
                                    startIcon={<AddIcon />}
                                    text='Agregar Nuevo'
                                    variant='outlined'
                                />
                            </Box>
                            <Paper variant='outlined'>
                                <Box
                                    sx={{
                                        maxHeight: 300,
                                        overflow: 'auto',
                                        '& table': { borderCollapse: 'collapse', width: '100%' },
                                        '& th': {
                                            border: '1px solid #9e9e9e',
                                            padding: '5px 10px',
                                            fontSize: '0.80rem',
                                            backgroundColor: '#e8eaf6',
                                            fontWeight: 'bold',
                                        },
                                        '& td': {
                                            border: '1px solid #c8c8c8',
                                            padding: '3px 8px',
                                            fontSize: '0.80rem',
                                        },
                                    }}
                                >
                                    <Table size='small'>
                                        <TableHead>
                                            <TableRow>
                                                {headCellsGastos.map((h) => (
                                                    <TableCell key={h.id}>{h.label}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {otrosGastos.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} sx={{ textAlign: 'center', color: '#888' }}>
                                                        Sin otros gastos registrados
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                [...otrosGastos].sort((a, b) => new Date(b.fechaPago) - new Date(a.fechaPago)).map((g, idx) => (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <TableRow
                                                        key={g.id ?? g.localId ?? idx}
                                                        sx={(g.id === null || !!g.idMovimientoBancario) ? { backgroundColor: '#e8f5e9' } : {}}
                                                    >
                                                        <TableCell>{g.tramoId}</TableCell>
                                                        <TableCell>{g.tipoPago?.id ?? g.tipoPago}</TableCell>
                                                        <TableCell>{g.descripcion}</TableCell>
                                                        <TableCell>{formatFecha(g.fechaPago)}</TableCell>
                                                        <TableCell align='right'>{formatMoneda(g.cantidad)}</TableCell>
                                                        <TableCell>{g.idMovimientoBancario ?? ''}</TableCell>
                                                        <TableCell>{g.idCuentaBancaria ?? ''}</TableCell>
                                                        <TableCell>
                                                            <Controls.ActionButton
                                                                color='primary'
                                                                onClick={() => { setGastoParaEditar(g); setOpenGastosPopup(true); }}
                                                            >
                                                                <EditOutlinedIcon fontSize='small' />
                                                            </Controls.ActionButton>
                                                            <Controls.ActionButton
                                                                color='primary'
                                                                onClick={() => eliminarGasto(g)}
                                                            >
                                                                <DeleteForeverOutlined fontSize='small' />
                                                            </Controls.ActionButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                        {otrosGastos.length > 0 && (
                                            <TableFooter>
                                                <TableRow sx={{ backgroundColor: '#e8eaf6' }}>
                                                    <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                                        Total
                                                    </TableCell>
                                                    <TableCell align='right' sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                                        {formatMoneda(
                                                            otrosGastos.reduce((sum, g) => sum + (parseFloat(g.cantidad) || 0), 0),
                                                        )}
                                                    </TableCell>
                                                    <TableCell colSpan={2} />
                                                </TableRow>
                                            </TableFooter>
                                        )}
                                    </Table>
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Form>

            {/* ---- Modal para Agregar/Editar gasto (mismo GastosForm que usa Viaje) ---- */}
            <Popup
                openPopup={openGastosPopup}
                setOpenPopup={setOpenGastosPopup}
                title='Gastos'
            >
                <GastosForm
                    addOrEdit={addOrEditGasto}
                    recordForEdit={gastoParaEditar}
                    valuesViaje={tramos}
                />
            </Popup>

            {/* ---- Diálogo de confirmación: borrar control ingreso al desmarcar ---- */}
            <Dialog onClose={handleCancelDeleteControlIngreso} open={deleteControlIngresoPending}>
                <DialogTitle>Confirmar acción</DialogTitle>
                <DialogContent>
                    <Typography variant='body2'>
                        El registro relacionado en Control Ingresos será borrado.
                        ¿Está seguro de continuar?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        color='default'
                        onClick={handleCancelDeleteControlIngreso}
                        text='Cancelar'
                        variant='outlined'
                    />
                    <Controls.Button
                        onClick={handleConfirmDeleteControlIngreso}
                        text='Proceder a borrar'
                    />
                </DialogActions>
            </Dialog>

            {/* ---- Diálogo de des-aprobación con password ---- */}
            <Dialog onClose={() => setUnapproveDialogOpen(false)} open={unapproveDialogOpen}>
                <DialogTitle>Registro APROBADO</DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ mb: 2, color: '#c62828', fontWeight: 'bold' }}
                        variant='body2'
                    >
                        Este registro ya está APROBADO. Necesitas introducir el password si quieres
                        editarlo. Al editarlo YA NO ESTARÁ APROBADO.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label='Password'
                        margin='dense'
                        onChange={(e) => setUnapprovePassword(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && unapprovePassword) handleUnapproveAndSave(); }}
                        type='password'
                        value={unapprovePassword}
                    />
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        color='default'
                        onClick={() => {
                            setUnapproveDialogOpen(false);
                            setUnapprovePassword('');
                            setPendingValues(null);
                        }}
                        text='Cancelar'
                        variant='outlined'
                    />
                    <Controls.Button
                        disabled={!unapprovePassword}
                        onClick={handleUnapproveAndSave}
                        text='Confirmar y Guardar'
                    />
                </DialogActions>
            </Dialog>

            <Dialog onClose={() => setGastosRequeridosDialogOpen(false)} open={gastosRequeridosDialogOpen}>
                <DialogTitle>Gasto requerido</DialogTitle>
                <DialogContent>
                    <Typography>
                        Modificaste el campo
                        {' '}
                        <strong>Retiro</strong>
                        {' '}
                        pero no has capturado ningún Gasto en la sección de Otros Gastos.
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                        Por favor captura al menos un gasto antes de guardar.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        onClick={() => setGastosRequeridosDialogOpen(false)}
                        text='Entendido'
                        variant='contained'
                    />
                </DialogActions>
            </Dialog>

            <Dialog onClose={() => setViajeAprobadoDialogOpen(false)} open={viajeAprobadoDialogOpen}>
                <DialogTitle>No se pueden actualizar los Gastos</DialogTitle>
                <DialogContent>
                    <Typography>
                        El viaje asociado a este folio ya fue
                        {' '}
                        <strong>aprobado</strong>
                        , por lo que no se pueden modificar sus gastos.
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                        Si desea editar los gastos de este folio, primero debe ir a la pantalla de
                        {' '}
                        <strong>Viajes</strong>
                        {' '}
                        y DES-aprobarlo.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        onClick={() => setViajeAprobadoDialogOpen(false)}
                        text='Entendido'
                        variant='contained'
                    />
                </DialogActions>
            </Dialog>

            {/* ---- Confirmación borrar gasto ---- */}
            <Dialog onClose={() => setConfirmDeleteGastoOpen(false)} open={confirmDeleteGastoOpen}>
                <DialogTitle>¿Borrar registro?</DialogTitle>
                <DialogContent>
                    <Typography>
                        El registro sera borrado de Otros Gastos, y ya no se podra recuperar.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        color='default'
                        onClick={() => setConfirmDeleteGastoOpen(false)}
                        text='Cancelar'
                        variant='outlined'
                    />
                    <Controls.Button
                        onClick={confirmarEliminarGasto}
                        sx={{ backgroundColor: '#c62828', color: 'white', '&:hover': { backgroundColor: '#b71c1c' } }}
                        text='Borrar'
                    />
                </DialogActions>
            </Dialog>
        </>
    );
}
